// src/components/panel/MonthlyReportButton.js
import React, { useState } from 'react';
import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { Button, message, Progress, Tooltip } from 'antd';
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { isAdminRole } from '../../permissions';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

function MonthlyReportButton() {
    const { user } = useAuth();
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadStatus, setDownloadStatus] = useState('idle');

    // Frontend redundancy — Dashboard is already admin-only route
    if (!isAdminRole(user?.role)) {
        return null;
    }

    const getCurrentMonthParams = () => {
        const now = new Date();
        return {
            year: now.getFullYear(),
            month: now.getMonth() + 1,
        };
    };

    const handleDownloadReport = async () => {
        if (isDemoMode) {
            message.success('✅ Reporte descargado para análisis Contable', 3);
            return;
        }
        try {
            setDownloading(true);
            setDownloadStatus('requesting');
            setDownloadProgress(0);
            message.loading('Iniciando generación de reporte mensual...', 0);

            const token = localStorage.getItem('token');
            const { year, month } = getCurrentMonthParams();

            // Step 1: Request generation
            const requestResponse = await axios.get(
                `${NODE_BACKEND_API_URL}/monthly-report/request?year=${year}&month=${month}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const { reportId } = requestResponse.data;

            if (!reportId) {
                throw new Error('No se recibió ID de reporte');
            }

            message.destroy();
            message.loading('Generando reporte Excel...', 0);
            setDownloadStatus('processing');

            // Step 2: Poll status
            await pollReportStatus(reportId, token);

        } catch (err) {
            message.destroy();
            console.error('Error downloading monthly report:', err);
            setDownloadStatus('failed');

            if (err.response?.status === 401) {
                message.error('Sesión expirada. Por favor, inicie sesión nuevamente.');
            } else if (err.response?.status === 500) {
                message.error('Error en el servidor. Por favor, intente más tarde.');
            } else {
                message.error('Error al generar el reporte mensual');
            }
        } finally {
            setDownloading(false);
        }
    };

    const pollReportStatus = async (reportId, token) => {
        const maxAttempts = 60;
        let attempts = 0;

        const checkStatus = async () => {
            try {
                attempts++;

                const statusResponse = await axios.get(
                    `${NODE_BACKEND_API_URL}/monthly-report/status/${reportId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                const { status, progress, error: jobError } = statusResponse.data;

                setDownloadProgress(progress || 0);

                if (status === 'completed') {
                    message.destroy();
                    message.loading('Descargando archivo...', 0);
                    await downloadFile(reportId, token);

                    message.destroy();
                    message.success('Reporte mensual descargado correctamente');
                    setDownloadStatus('completed');

                    setTimeout(() => {
                        setDownloadStatus('idle');
                        setDownloadProgress(0);
                    }, 2000);

                } else if (status === 'failed') {
                    message.destroy();
                    throw new Error(jobError || 'Generación fallida');

                } else if (attempts >= maxAttempts) {
                    message.destroy();
                    throw new Error('Tiempo de espera agotado');

                } else {
                    setTimeout(checkStatus, 2000);
                }

            } catch (err) {
                message.destroy();
                console.error('Error checking report status:', err);
                setDownloadStatus('failed');
                throw err;
            }
        };

        await checkStatus();
    };

    const downloadFile = async (reportId, token) => {
        try {
            const response = await axios.get(
                `${NODE_BACKEND_API_URL}/monthly-report/download/${reportId}`,
                {
                    responseType: 'blob',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            const { year, month } = getCurrentMonthParams();
            const filename = contentDisposition
                ? contentDisposition.split('filename=')[1].replace(/"/g, '')
                : `Reporte_Mensual_${year}-${String(month).padStart(2, '0')}.xlsx`;

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error('Error downloading report file:', err);
            throw new Error('Error al descargar el archivo');
        }
    };

    const { month, year } = getCurrentMonthParams();
    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    const monthLabel = `${monthNames[month - 1]} ${year}`;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title={`Generar reporte Excel de ${monthLabel}`}>
                <Button
                    type="primary"
                    icon={downloading ? <FileExcelOutlined /> : <DownloadOutlined />}
                    onClick={handleDownloadReport}
                    loading={downloading && downloadStatus === 'requesting'}
                    disabled={downloading}
                    size="middle"
                    style={{
                        backgroundColor: '#1677ff',
                        borderColor: '#1677ff',
                    }}
                >
                    {downloading
                        ? downloadStatus === 'requesting'
                            ? 'Iniciando...'
                            : `Reporte... ${downloadProgress}%`
                        : `Reporte Mensual`
                    }
                </Button>
            </Tooltip>
            {downloading && downloadStatus === 'processing' && (
                <Progress
                    percent={downloadProgress}
                    size="small"
                    status="active"
                    style={{ width: '80px', marginBottom: 0 }}
                    showInfo={false}
                />
            )}
        </div>
    );
}

export default MonthlyReportButton;
